import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import NewAutoSMSForward from './NewAutoSMSForward';
import ImmediateChangesNotice from '../shared/ImmediateChangesNotice';
import SkeletonTable from '../../shared/SkeletonTable';
import { classes } from '../styles';
import { IAutoSmsForward, IStatusTransitionsProps } from '../../types';
import { IApplicationStatus } from '../../../Job/types';
import { immutableRemove } from '../../../Components/Utilities/immutableRemove';
import RecruitApiClient from '../../../../../components/RecruitApiClient';

export default function AutoSMSForward({ jobId, setSnackbar }: IStatusTransitionsProps) {
  const [loading, setLoading] = useState({
    autoForwards: true,
    applicationStatuses: true,
    templatesList: true
  });
  const [isDeleting, setIsDeleting] = useState(-1);
  const [autoForwards, setAutoForwards] = useState<IAutoSmsForward[]>([]);
  const [applicationStatuses, setApplicationStatuses] = useState<IApplicationStatus[]>([]);
  const [templates, setTemplates] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [forwardIndex, setForwardIndex] = useState(0);

  const getAutoForwards = useCallback(async () => {
    setLoading((prev) => ({ ...prev, autoForwards: true }));
    try {
      const {
        res: { auto_sms_forwards }
      } = await RecruitApiClient.get<{ auto_sms_forwards: IAutoSmsForward[] }>(
        `/api/v4/jobs/${jobId}/auto_sms_forwards`
      );
      setAutoForwards(auto_sms_forwards);
    } catch (error: any) {
      setSnackbar({
        message: error.message || 'Error fetching auto SMS forwards',
        state: 'error'
      });
    } finally {
      setLoading((prev) => ({ ...prev, autoForwards: false }));
    }
  }, [jobId, setSnackbar]);

  const getApplicationStatuses = useCallback(async () => {
    setLoading((prev) => ({ ...prev, applicationStatuses: true }));
    try {
      const {
        res: { job_application_statuses }
      } = await RecruitApiClient.get<{ job_application_statuses: IApplicationStatus[] }>(
        `/api/v4/jobs/${jobId}/job_application_statuses`
      );
      setApplicationStatuses(job_application_statuses);
    } catch (error: any) {
      setSnackbar({
        message: error.message || 'Error fetching application statuses',
        state: 'error'
      });
    } finally {
      setLoading((prev) => ({ ...prev, applicationStatuses: false }));
    }
  }, [jobId, setSnackbar]);

  const getTemplatesAndPlaceholders = useCallback(async () => {
    setLoading((prev) => ({ ...prev, templatesList: true }));
    try {
      const {
        res: { sms_templates }
      } = await RecruitApiClient.get<{ sms_templates: [] }>(
        `/api/v4/jobs/${jobId}/sms_templates?kind=custom`
      );
      setTemplates(sms_templates);
    } catch (error) {
      setSnackbar({
        message: 'Error fetching templates',
        state: 'error'
      });
    } finally {
      setLoading((prev) => ({ ...prev, templatesList: false }));
    }
  }, [jobId, setSnackbar]);

  const removeAutoForwards = async (index: number) => {
    setIsDeleting(index);
    try {
      await RecruitApiClient.delete(
        `/api/v4/jobs/${jobId}/auto_sms_forwards/${autoForwards[index].id}`
      );
      setSnackbar({
        message: 'Auto SMS forward deleted successfully',
        state: 'success'
      });
      setAutoForwards(immutableRemove(autoForwards, index));
    } catch (error: any) {
      setSnackbar({
        message: error.message || 'Error deleting auto SMS forward',
        state: 'error'
      });
    } finally {
      setIsDeleting(-1);
    }
  };

  const handleOpenModal = (index: number) => {
    setForwardIndex(index);
    setModalOpen(true);
  };

  useEffect(() => {
    getAutoForwards();
    getApplicationStatuses();
    getTemplatesAndPlaceholders();
  }, [getApplicationStatuses, getAutoForwards, getTemplatesAndPlaceholders]);

  return (
    <Box sx={classes.automationContainer}>
      <Box sx={classes.titleAndButtonContainer}>
        <h2>Auto SMS forward</h2>
        <Button
          id="new-auto-sms-forward-button"
          data-testid="new-auto-sms-forward-button"
          sx={classes.newButton}
          onClick={() => handleOpenModal(-1)}
        >
          New auto SMS forward
        </Button>
      </Box>
      <ImmediateChangesNotice />
      {loading.autoForwards || (autoForwards && autoForwards.length > 0) ? (
        <TableContainer sx={classes.tableContainer}>
          <Table sx={classes.table} aria-label="auto sms forwards table">
            <TableHead>
              <TableRow>
                <TableCell>Trigger status</TableCell>
                <TableCell>Phones</TableCell>
                <TableCell align="right">{/*Action column*/}</TableCell>
              </TableRow>
            </TableHead>
            {loading.autoForwards || loading.applicationStatuses ? (
              <SkeletonTable rows={5} cols={3} />
            ) : (
              <TableBody>
                {autoForwards.map((forward, index) => (
                  <TableRow key={forward.id}>
                    <TableCell>
                      {applicationStatuses.find((status) => status.id === forward.trigger_status_id)
                        ?.name ?? '-'}
                    </TableCell>
                    <TableCell>{forward.phones.join(', ') || '-'}</TableCell>
                    <TableCell sx={classes.tableActions}>
                      <IconButton
                        id="auto-sms-forward-edit-button"
                        data-testid="auto-sms-forward-edit-button"
                        sx={classes.tableEditButton}
                        onClick={() => handleOpenModal(index)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        id="auto-sms-forward-delete-button"
                        data-testid="auto-sms-forward-delete-button"
                        sx={classes.tableDeleteButton}
                        onClick={() => removeAutoForwards(index)}
                      >
                        {isDeleting === index ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          <DeleteIcon />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      ) : (
        <Box sx={classes.noAutoForwarders}>
          No auto forwards configured. Click on the button above to create one.
        </Box>
      )}
      {!loading.autoForwards && !loading.applicationStatuses && (
        <NewAutoSMSForward
          jobId={jobId}
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          autoForwards={autoForwards}
          setAutoForwards={setAutoForwards}
          applicationStatuses={applicationStatuses || []}
          smsTemplates={templates}
          forwardIndex={modalOpen ? forwardIndex : -1}
          isLoadingTemplateList={loading.templatesList}
          setSnackbar={setSnackbar}
        />
      )}
    </Box>
  );
}
